<template>
  <div class="person-input-card" :style="cardStyle">
    <v-btn v-if="deletable" icon small class="person-input-card__delete-btn" :style="{ color: accentColor }" @click="$emit('delete')">
      <v-icon small>delete</v-icon>
    </v-btn>

    <h3 v-if="title" class="person-input-card__title">{{ title }}</h3>

    <v-text-field
      v-mask="['###.###.###-##']"
      :value="value.documentNo"
      outlined
      dense
      :hide-details="!fieldErrors.documentNo"
      :error-messages="fieldErrors.documentNo ? [fieldErrors.documentNo] : []"
      label="CPF"
      :disabled="disableCpf || disabled"
      :style="{ color: fontColor }"
      class="person-input-card__input"
      @input="updateField('documentNo', $event)"
    />
    <v-text-field
      :value="value.name"
      outlined
      dense
      :hide-details="!fieldErrors.name"
      :error-messages="fieldErrors.name ? [fieldErrors.name] : []"
      label="Nome"
      :disabled="disabled"
      :style="{ color: fontColor }"
      class="person-input-card__input"
      @input="updateField('name', $event)"
    />
    <v-text-field
      v-mask="['(##) ####-#####', '(##) # ####-####']"
      :value="value.phone"
      outlined
      dense
      :hide-details="!fieldErrors.phone"
      :error-messages="fieldErrors.phone ? [fieldErrors.phone] : []"
      label="Telefone"
      :disabled="disabled"
      :style="{ color: fontColor }"
      class="person-input-card__input"
      @input="updateField('phone', $event)"
    />
  </div>
</template>

<script>
import { getContrastRatio } from '@/helpers/colors'
import { validateCpf } from '@/config/custom-validators'

export default {
  name: 'PersonInputCard',
  props: {
    value: {
      type: Object,
      default: () => ({
        documentNo: '',
        name: '',
        phone: ''
      })
    },
    title: {
      type: String,
      default: ''
    },
    deletable: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    disableCpf: {
      type: Boolean,
      default: false
    },
    fontColor: {
      type: String,
      default: '#000000'
    },
    backgroundColor: {
      type: String,
      default: '#f5f5f5'
    }
  },
  data: () => ({
    fieldErrors: {
      documentNo: null,
      name: null,
      phone: null
    }
  }),
  computed: {
    accentColor() {
      // Pick between dark-grayish (#666666) and light-grayish (#cccccc) based on contrast with background
      const darkGray = '#666666'
      const lightGray = '#cccccc'
      const darkContrast = getContrastRatio(darkGray, this.backgroundColor)
      const lightContrast = getContrastRatio(lightGray, this.backgroundColor)
      return darkContrast > lightContrast ? darkGray : lightGray
    },
    borderColor() {
      // Same logic for border
      const darkGray = '#999999'
      const lightGray = '#e0e0e0'
      const darkContrast = getContrastRatio(darkGray, this.backgroundColor)
      const lightContrast = getContrastRatio(lightGray, this.backgroundColor)
      return darkContrast > lightContrast ? darkGray : lightGray
    },
    cardStyle() {
      return {
        borderColor: this.borderColor
      }
    }
  },
  methods: {
    updateField(field, value) {
      this.$emit('input', {
        ...this.value,
        [field]: value
      })
      // Clear error when user starts typing
      if (this.fieldErrors[field]) {
        this.fieldErrors[field] = null
      }
    },
    validateName(name) {
      if (!name || name.trim() === '') {
        return 'Nome é obrigatório'
      }
      return null
    },
    validateCpfField(cpf) {
      if (!cpf || cpf.trim() === '') {
        return 'CPF é obrigatório'
      }
      const cleanedCpf = cpf.replace(/\D/g, '')
      if (!validateCpf(cleanedCpf)) {
        return 'CPF inválido'
      }
      return null
    },
    validatePhone(phone) {
      if (!phone || phone.trim() === '') {
        return 'Telefone é obrigatório'
      }
      const cleaned = phone.replace(/\D/g, '')
      // Remove DDI (55) if present
      let phoneDigits = cleaned
      if (cleaned.length >= 12 && cleaned.startsWith('55')) {
        phoneDigits = cleaned.substring(2)
      }
      // Check if phone has valid length (10 for landline, 11 for mobile)
      if (phoneDigits.length !== 10 && phoneDigits.length !== 11) {
        return 'Telefone inválido'
      }
      // Check if phone is not all same digits
      if (phoneDigits.length > 0 && new Set(phoneDigits.split('')).size === 1) {
        return 'Telefone inválido'
      }
      return null
    },
    validate() {
      this.fieldErrors = {
        documentNo: this.disableCpf ? null : this.validateCpfField(this.value.documentNo),
        name: this.validateName(this.value.name),
        phone: this.validatePhone(this.value.phone)
      }
      return !this.fieldErrors.documentNo && !this.fieldErrors.name && !this.fieldErrors.phone
    },
    clearErrors() {
      this.fieldErrors = {
        documentNo: null,
        name: null,
        phone: null
      }
    }
  }
}
</script>

<style scoped lang="scss">
.person-input-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border: 1px solid;
  border-radius: 8px;
  background-color: #ffffff;
  width: 100%;
}

.person-input-card__delete-btn {
  position: absolute;
  top: 8px;
  right: 8px;
}

.person-input-card__title {
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 4px;
  text-align: center;
  color: inherit;
}

.person-input-card__input {
  width: 100%;
}
</style>
